import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { WysiwygEditor } from '@/components/WysiwygEditor';
import { toast } from 'sonner';
import axios from 'axios';
import { Lightbox } from '@/components/Lightbox';
import { 
  ArrowLeft,
  MapPin, 
  Clock,
  Calendar,
  ExternalLink,
  Download,
  Edit2,
  Users,
  UserPlus,
  UserMinus,
  CheckCircle,
  AlertCircle,
  Loader2,
  Mail,
  FileDown,
  X,
  Trash2,
  CreditCard,
  Banknote,
  QrCode,
  Ticket,
  User,
  Phone,
  Share2,
  Copy,
  Check,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate iCal content
const generateICalContent = (event) => {
  const formatDate = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    if (timeStr) {
      const [hours, minutes] = timeStr.split(':');
      date.setHours(parseInt(hours), parseInt(minutes), 0);
    }
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const startDate = formatDate(event.date, event.time);
  let endDate;
  if (event.time) {
    const endDateTime = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    endDateTime.setHours(parseInt(hours) + 2, parseInt(minutes), 0);
    endDate = endDateTime.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  } else {
    const nextDay = new Date(event.date);
    nextDay.setDate(nextDay.getDate() + 1);
    endDate = nextDay.toISOString().split('T')[0].replace(/-/g, '');
  }
  
  const uid = `${event.id}@shrt.ch`;
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  
  let description = event.lead || event.description || '';
  if (event.link) {
    description += (description ? '\\n\\n' : '') + 'Link: ' + event.link;
  }
  
  const icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SHRT//Event Calendar//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    event.time ? `DTSTART:${startDate}` : `DTSTART;VALUE=DATE:${event.date.replace(/-/g, '')}`,
    event.time ? `DTEND:${endDate}` : `DTEND;VALUE=DATE:${endDate}`,
    `SUMMARY:${event.title.replace(/,/g, '\\,')}`,
    description ? `DESCRIPTION:${description.replace(/\n/g, '\\n').replace(/,/g, '\\,')}` : '',
    event.location ? `LOCATION:${event.location.replace(/,/g, '\\,')}` : '',
    event.link ? `URL:${event.link}` : '',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(line => line).join('\r\n');
  
  return icalContent;
};

const downloadICalFile = (event) => {
  const content = generateICalContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function EventDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [guests, setGuests] = useState(0);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' or 'cash'
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState({ card: true, twint: true, cash: true });
  const [galleryIndex, setGalleryIndex] = useState(null);
  
  // Admin state
  const [registrations, setRegistrations] = useState(null);
  const [showAdminAddForm, setShowAdminAddForm] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [sendToAllMembers, setSendToAllMembers] = useState(true);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');

  // Handle payment return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Zahlung abgebrochen');
      // Clear URL params
      navigate(`/events/${id}`, { replace: true });
    }
  }, [searchParams, id]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Zahlungsstatus konnte nicht überprüft werden. Bitte kontaktieren Sie uns.');
      setPaymentProcessing(false);
      navigate(`/events/${id}`, { replace: true });
      return;
    }
    
    setPaymentProcessing(true);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/event/${id}/status?session_id=${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check for successful payment - either 'paid' status or 'complete' status (already processed)
      if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
        toast.success('Zahlung erfolgreich! Sie sind angemeldet.');
        setPaymentProcessing(false);
        // Clear URL params and refresh event data
        navigate(`/events/${id}`, { replace: true });
        fetchEvent();
      } else if (response.data.status === 'expired') {
        toast.error('Zahlungssession abgelaufen');
        setPaymentProcessing(false);
        navigate(`/events/${id}`, { replace: true });
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= maxAttempts - 1) {
        setPaymentProcessing(false);
        toast.info('Zahlung wird verarbeitet. Bitte laden Sie die Seite neu.');
        navigate(`/events/${id}`, { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  useEffect(() => {
    fetchEvent();
    // Fetch active payment methods
    axios.get(`${API_URL}/api/payment-methods`)
      .then(res => {
        setAvailablePaymentMethods(res.data);
        // Set default payment method based on what's available
        const hasOnline = res.data.card || res.data.twint;
        if (!hasOnline && res.data.cash) setPaymentMethod('cash');
        else if (hasOnline) setPaymentMethod('online');
      })
      .catch(() => {});
  }, [id]);

  const fetchEvent = async () => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/events/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvent(response.data);
      
      // Pre-fill form if already registered
      if (response.data.my_registration) {
        setGuests(response.data.my_registration.guests || 0);
        setNotes(response.data.my_registration.notes || '');
      }
    } catch (error) {
      toast.error('Event nicht gefunden');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/events/${id}/registrations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Anmeldungen');
    }
  };

  const handleRegister = async () => {
    // Check if payment is required and online payment is selected
    if (event.payment_required && event.price > 0 && paymentMethod === 'online') {
      await handlePaymentRegistration();
      return;
    }
    
    setRegistering(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${id}/register`,
        { guests, notes, payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowRegisterModal(false);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Anmeldung');
    } finally {
      setRegistering(false);
    }
  };

  const handlePaymentRegistration = async () => {
    setRegistering(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/event/checkout`,
        { 
          event_id: id,
          origin_url: window.location.origin,
          guests,
          notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Zahlung');
      setRegistering(false);
    }
  };

  const handleUpdateRegistration = async () => {
    setRegistering(true);
    const token = getToken();
    
    try {
      const response = await axios.put(
        `${API_URL}/api/events/${id}/register`,
        { guests, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowRegisterModal(false);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setRegistering(false);
    }
  };

  const handleUnregister = async () => {
    if (!confirm('Anmeldung wirklich stornieren?')) return;
    setRegistering(true);
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/events/${id}/register`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Anmeldung storniert');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    } finally {
      setRegistering(false);
    }
  };

  const generateEventCampaignContent = () => {
    if (!event) return '';
    const evDate = new Date(event.date);
    const dateStrShort = evDate.toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' });
    const dateStrFull = evDate.toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const eventUrl = `https://member.shrt.ch/login?returnUrl=${encodeURIComponent('/events/' + event.id)}`;
    
    let html = `<h2 style="margin-bottom:0;">Einladung</h2>`;
    html += `<h2 style="margin-top:0;margin-bottom:0;">S.H.R.T. – ${event.title}</h2>`;
    html += `<h2 style="margin-top:0;">${dateStrShort}</h2>`;
    if (event.location) html += `<p><strong>${event.location}</strong></p>`;
    if (event.payment_required && event.price > 0) {
      html += `<p><strong>Teilnahmegebühr:</strong> CHF ${event.price.toFixed(2)} pro Person</p>`;
    }
    if (event.lead) html += `<p>${event.lead}</p>`;
    // Include full event description (WYSIWYG content)
    const fullDescription = event.body_html || event.content || '';
    if (fullDescription) {
      html += fullDescription;
    }
    // Include attachments as download links
    const attachments = event.attachments || [];
    if (attachments.length > 0) {
      html += `<p style="margin-top:20px;"><strong>Dokumente:</strong></p>`;
      html += `<ul style="margin:8px 0;padding-left:20px;">`;
      for (const att of attachments) {
        const icon = att.file_type === 'pdf' ? '📄' : '🖼';
        html += `<li style="margin-bottom:6px;"><a href="${att.file_url}" target="_blank" style="color:#d5001c;text-decoration:underline;">${icon} ${att.filename}</a></li>`;
      }
      html += `</ul>`;
    }
    // Include organizer contact info
    if (event.organizer) {
      html += `<p style="margin-top:20px;"><strong>Organisator (Kontaktperson):</strong> ${event.organizer.name}`;
      if (event.organizer.email) html += ` · <a href="mailto:${event.organizer.email}" style="color:#d5001c;">${event.organizer.email}</a>`;
      if (event.organizer.mobile) html += ` · <a href="tel:${event.organizer.mobile}" style="color:#d5001c;">${event.organizer.mobile}</a>`;
      html += `</p>`;
    }
    const guestUrl = `https://member.shrt.ch/public/event/${event.id}`;
    html += `<!-- SHRT_EVENT_BUTTON_START --><table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:30px auto;">`;
    html += `<tr><td style="padding:4px 0;" align="center"><a href="${eventUrl}" target="_blank" class="shrt-email-button" style="background-color:#d5001c;color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;font-size:13px;font-weight:bold;letter-spacing:1px;padding:16px 28px;display:inline-block;">ALS MITGLIED ANMELDEN</a></td></tr>`;
    html += `<tr><td style="padding:4px 0;" align="center"><a href="${guestUrl}" target="_blank" class="shrt-email-button" style="background-color:#1a1a1a;color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;font-size:13px;font-weight:bold;letter-spacing:1px;padding:16px 28px;display:inline-block;">ALS GAST ANMELDEN</a></td></tr>`;
    html += `</table><!-- SHRT_EVENT_BUTTON_END -->`;
    return html;
  };

  const handleCreateEventCampaign = async () => {
    setCreatingCampaign(true);
    const token = getToken();
    
    try {
      const evDate = new Date(event.date);
      const subjDate = evDate.toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' });
      const contentHtml = generateEventCampaignContent();
      const response = await axios.post(
        `${API_URL}/api/newsletter/campaigns`,
        {
          name: `Event: ${event.title}`,
          subject: `S.H.R.T. – ${event.title}: ${subjDate}`,
          content_html: contentHtml
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const campaignId = response.data.campaign.id;
      toast.success('Kampagne erstellt');
      setShowNewsletterModal(false);
      setSendToAllMembers(false);
      navigate(`/admin/newsletter-system/edit/${campaignId}?preselect_all_members=true`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Kampagne');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterSubject || !newsletterContent) {
      toast.error('Betreff und Inhalt sind erforderlich');
      return;
    }
    
    setSendingNewsletter(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${id}/newsletter`,
        {
          subject: newsletterSubject,
          content_html: newsletterContent,
          include_waitlist: true,
          send_to_all_members: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowNewsletterModal(false);
      setNewsletterSubject('');
      setNewsletterContent('');
      setSendToAllMembers(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleExport = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/events/${id}/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `event_${id}_registrations.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Export');
    }
  };

  const handleAdminCancelRegistration = async (registrationId) => {
    if (!confirm('Anmeldung wirklich stornieren?')) return;
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/events/${id}/registrations/${registrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Anmeldung storniert');
      fetchRegistrations();
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    }
  };

  const handleConfirmWaitlist = async (registrationId) => {
    const token = getToken();
    
    try {
      await axios.post(
        `${API_URL}/api/events/${id}/registrations/${registrationId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Anmeldung bestätigt');
      fetchRegistrations();
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Bestätigen');
    }
  };

  if (loading || paymentProcessing) {
    return (
      <Layout>
        <div className=" flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          {paymentProcessing && (
            <p className="text-muted-foreground">Zahlung wird überprüft...</p>
          )}
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  const eventDate = new Date(event.date);
  const isPast = eventDate < new Date();
  const stats = event.stats || {};
  const myReg = event.my_registration;
  const isRegistered = !!myReg;
  const isWaitlist = myReg?.status === 'waitlist';
  const maxParticipants = event.max_participants;
  const isFull = maxParticipants && stats.total_participants >= maxParticipants;
  const isRegistrationOpen = event.registration_open !== false;
  const requiresPayment = event.payment_required && event.price > 0;
  const totalPrice = requiresPayment ? (1 + guests) * event.price : 0;

  return (
    <Layout>
      <div className="max-w-4xl w-full overflow-hidden">
        {/* Back Button */}
        <Link 
          to="/events" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          Zurück zu Events
        </Link>

        {/* Event Image */}
        {(event.image || event.image_url) && (
          <div className="mb-8 -mx-4 sm:-mx-6 lg:-mx-8">
            <img 
              src={(event.image || event.image_url).startsWith('/') ? `${API_URL}${event.image || event.image_url}` : (event.image || event.image_url)}
              alt={event.title}
              className="w-full h-48 sm:h-64 md:h-80 object-cover"
            />
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-muted-foreground">
            <span className={`flex items-center gap-1 ${isPast ? '' : 'text-primary'}`}>
              <Calendar size={16} />
              {eventDate.toLocaleDateString('de-CH', { 
                weekday: 'long',
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            {event.time && (
              <span className="flex items-center gap-1">
                <Clock size={16} />
                {event.time} Uhr
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin size={16} />
                {event.location}
              </span>
            )}
          </div>

          <h1 className="font-['Oswald'] text-2xl sm:text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight mb-4">
            {event.title}
          </h1>

          {event.lead && (
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {event.lead}
            </p>
          )}
        </div>

        {/* Organizer */}
        {event.organizer && (
          <div className="bg-card border border-border p-4 mb-6" data-testid="event-organizer-section">
            <div className="flex items-center gap-2 mb-2">
              <User size={18} className="text-primary" />
              <span className="font-semibold text-sm uppercase tracking-wider">Organisator (Kontaktperson)</span>
            </div>
            <div className="ml-6 space-y-1">
              <p className="font-medium">{event.organizer.name}</p>
              {event.organizer.email && (
                <a href={`mailto:${event.organizer.email}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
                  <Mail size={14} /> {event.organizer.email}
                </a>
              )}
              {event.organizer.mobile && (
                <a href={`tel:${event.organizer.mobile}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
                  <Phone size={14} /> {event.organizer.mobile}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Registration Stats */}
        {maxParticipants && (
          <div className="bg-card border border-border p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={20} className="text-primary" />
                <span className="font-semibold">Teilnehmer</span>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold">{stats.total_participants || 0}</span>
                <span className="text-muted-foreground">/{maxParticipants}</span>
                {stats.total_waitlist > 0 && (
                  <div className="text-sm text-yellow-600">+{stats.total_waitlist} Warteliste</div>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.min(100, (stats.total_participants / maxParticipants) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Price Info */}
        {requiresPayment && (
          <div className="bg-card border border-border p-4 mb-6 overflow-hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Banknote size={20} className="text-primary" />
                <span className="font-semibold">Teilnahmegebühr</span>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold">CHF {event.price?.toFixed(2)}</span>
                <span className="text-muted-foreground text-sm block">pro Person</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2 flex items-start gap-2">
              <CreditCard size={14} className="flex-shrink-0 mt-0.5" />
              <span className="break-words">Zahlung mit {[availablePaymentMethods.twint && 'TWINT', availablePaymentMethods.card && 'Kreditkarte', availablePaymentMethods.cash && 'Barzahlung'].filter(Boolean).join(', ')} möglich</span>
            </p>
          </div>
        )}

        {/* My Registration Status */}
        {isRegistered && !isPast && (
          <div className="mb-6 space-y-3">
            {/* Registration Status */}
            <div className={`p-4 border ${isWaitlist ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  {isWaitlist ? (
                    <AlertCircle className="text-yellow-600" size={20} />
                  ) : (
                    <CheckCircle className="text-green-600" size={20} />
                  )}
                  <span className="font-semibold">
                    {isWaitlist ? 'Sie stehen auf der Warteliste' : 'Sie sind angemeldet'}
                  </span>
                  {myReg.guests > 0 && (
                    <span className="text-sm text-muted-foreground">(+{myReg.guests} Gäste)</span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => setShowRegisterModal(true)}
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                  >
                    <Edit2 size={14} className="mr-1" />
                    Bearbeiten
                  </Button>
                  <Button
                    onClick={handleUnregister}
                    variant="outline"
                    size="sm"
                    className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white"
                    disabled={registering}
                  >
                    <UserMinus size={14} className="mr-1" />
                    Abmelden
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Prominent Ticket Download Button */}
            <Button
              onClick={async () => {
                const token = localStorage.getItem('shrt_token');
                try {
                  const response = await axios.get(`${API_URL}/api/events/${id}/ticket`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  const safeTitle = (event.title || 'event').replace(/[^a-zA-Z0-9]/g, '_');
                  link.setAttribute('download', `Ticket_${safeTitle}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  toast.success('Ticket heruntergeladen');
                } catch (error) {
                  console.error('Ticket download error:', error);
                  toast.error('Fehler beim Herunterladen des Tickets');
                }
              }}
              className="w-full h-14 rounded-none text-lg font-bold bg-primary hover:bg-primary/90"
              data-testid="download-ticket-btn"
            >
              <Ticket size={24} className="mr-3" />
              TICKET HERUNTERLADEN
              <Download size={20} className="ml-3" />
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 mb-8 pb-8 border-b border-border">
          {/* Anmeldung nur wenn Buchungssystem aktiv */}
          {!isPast && isRegistrationOpen && !isRegistered && (
            <Button
              onClick={() => setShowRegisterModal(true)}
              className={`rounded-none uppercase tracking-wider font-bold text-xs sm:text-sm col-span-2 sm:col-span-1 ${isFull ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : ''}`}
              data-testid="register-btn"
            >
              <UserPlus size={18} className="mr-2" />
              {isFull ? 'Auf Warteliste setzen' : 'Jetzt anmelden'}
            </Button>
          )}
          
          {/* Externer Link - primär wenn Buchungssystem deaktiviert */}
          {event.link && (
            <a
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 px-4 py-2 transition-colors uppercase tracking-wider font-bold text-xs sm:text-sm ${
                !isRegistrationOpen 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'border border-border hover:bg-muted'
              }`}
            >
              <ExternalLink size={18} />
              {event.link_text || 'Mehr erfahren'}
            </a>
          )}
          
          <Button
            onClick={() => downloadICalFile(event)}
            variant="outline"
            className="rounded-none uppercase tracking-wider font-bold text-xs sm:text-sm truncate"
          >
            <Download size={18} className="mr-2" />
            Zum Kalender
          </Button>

          {/* Share Button */}
          <div className="relative">
            <Button
              onClick={() => setShowShareMenu(!showShareMenu)}
              variant="outline"
              className="w-full sm:w-auto rounded-none uppercase tracking-wider font-bold text-xs sm:text-sm"
              data-testid="share-event-btn"
            >
              <Share2 size={18} className="mr-2" />
              Teilen
            </Button>
            {showShareMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowShareMenu(false)} />
                <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 mt-1 z-50 bg-card border border-border shadow-lg min-w-[200px]" data-testid="share-event-menu">
                  <ShareMenuContent event={event} eventId={id} onClose={() => setShowShareMenu(false)} linkCopied={linkCopied} setLinkCopied={setLinkCopied} />
                </div>
              </>
            )}
          </div>

          {isAdmin && (
            <>
              <Button
                onClick={() => navigate(`/events/${id}/edit`)}
                variant="outline"
                className="rounded-none uppercase tracking-wider font-bold text-xs sm:text-sm"
              >
                <Edit2 size={18} className="mr-2" />
                Bearbeiten
              </Button>
            </>
          )}
        </div>

        {/* Event Content */}
        {(event.body_html || event.content) && (
          <div 
            className="max-w-none mb-8 text-foreground overflow-hidden [overflow-wrap:anywhere]
              [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-base
              [&_h2]:font-['Oswald'] [&_h2]:text-xl [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-foreground
              [&_h3]:font-['Oswald'] [&_h3]:text-lg [&_h3]:font-bold [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-foreground
              [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc
              [&_ol]:my-4 [&_ol]:pl-6 [&_ol]:list-decimal
              [&_li]:mb-1 [&_li]:leading-relaxed
              [&_li_p]:mb-0 [&_li_p]:inline
              [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80
              [&_strong]:font-semibold [&_strong]:text-foreground"
            dangerouslySetInnerHTML={{ __html: event.body_html || event.content }}
          />
        )}

        {/* Dateianhänge */}
        {event.attachments && event.attachments.length > 0 && (
          <div className="mb-8" data-testid="event-attachments">
            <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 text-primary">
              Dokumente
            </h3>
            <div className="space-y-2">
              {event.attachments.map(att => (
                <a
                  key={att.id}
                  href={att.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 border border-border hover:border-primary/50 hover:bg-muted/20 transition-colors group"
                  data-testid={`attachment-link-${att.id}`}
                >
                  {att.file_type === 'pdf' ? (
                    <div className="w-10 h-10 bg-red-500/20 text-red-500 flex items-center justify-center text-xs font-bold flex-shrink-0">PDF</div>
                  ) : (
                    <img src={att.file_url} alt={att.filename} className="w-10 h-10 object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground group-hover:text-primary truncate block">{att.filename}</span>
                    <span className="text-xs text-muted-foreground">Herunterladen</span>
                  </div>
                  <Download size={16} className="text-muted-foreground group-hover:text-primary flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Rückblick Section */}
        {event.has_review && (
          <div className="mt-8 pt-8 border-t border-border">
            <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-6 text-primary">
              Rückblick
            </h2>
            
            {/* Review Text */}
            {event.review_text && (
              <div 
                className="max-w-none mb-8 text-foreground
                  [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-base
                  [&_h2]:font-['Oswald'] [&_h2]:text-xl [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-foreground
                  [&_h3]:font-['Oswald'] [&_h3]:text-lg [&_h3]:font-bold [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-foreground
                  [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc
                  [&_ol]:my-4 [&_ol]:pl-6 [&_ol]:list-decimal
                  [&_li]:mb-1 [&_li]:leading-relaxed
                  [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80
                  [&_strong]:font-semibold [&_strong]:text-foreground"
                dangerouslySetInnerHTML={{ __html: event.review_text }}
              />
            )}
            
            {/* Review PDFs */}
            {event.review_pdfs && event.review_pdfs.length > 0 && (
              <div className="mb-8" data-testid="review-pdfs">
                <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4">
                  Dokumente zum Rückblick
                </h3>
                <div className="space-y-2">
                  {event.review_pdfs.map((pdf, idx) => (
                    <a
                      key={idx}
                      href={pdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 border border-border hover:border-primary/50 hover:bg-muted/20 transition-colors group"
                      data-testid={`review-pdf-link-${idx}`}
                    >
                      <div className="w-10 h-10 bg-red-500/20 text-red-500 flex items-center justify-center text-xs font-bold flex-shrink-0">PDF</div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground group-hover:text-primary truncate block">{pdf.filename || `PDF ${idx + 1}`}</span>
                        <span className="text-xs text-muted-foreground">Herunterladen</span>
                      </div>
                      <Download size={16} className="text-muted-foreground group-hover:text-primary flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Review Image Gallery */}
            {event.review_images && event.review_images.length > 0 && (() => {
              const getThumb = (img) => typeof img === 'string' ? img : (img?.thumb || img?.url || img);
              const getFull = (img) => typeof img === 'string' ? img : (img?.url || img);
              return (
              <div>
                <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4">
                  Bildergalerie ({event.review_images.length} Bilder)
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {event.review_images.map((img, idx) => (
                    <div 
                      key={idx}
                      className="aspect-[4/3] overflow-hidden bg-muted cursor-pointer group"
                      onClick={() => setGalleryIndex(idx)}
                      data-testid={`gallery-image-${idx}`}
                    >
                      <img 
                        src={getThumb(img)} 
                        alt={`Rückblick Bild ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                {galleryIndex !== null && (
                  <Lightbox 
                    images={event.review_images.map(getFull)} 
                    initialIndex={galleryIndex} 
                    onClose={() => setGalleryIndex(null)} 
                  />
                )}
              </div>
              );
            })()}
          </div>
        )}

        {/* Admin Section: Participant List */}
        {isAdmin && (
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                Teilnehmerverwaltung
              </h2>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setShowAdminAddForm(!showAdminAddForm)}
                  size="sm"
                  className="rounded-none"
                  data-testid="admin-add-participant-btn"
                >
                  <UserPlus size={16} className="mr-2" />
                  {showAdminAddForm ? 'Abbrechen' : 'Teilnehmer hinzufügen'}
                </Button>
                <Button
                  onClick={() => { fetchRegistrations(); }}
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                >
                  <Users size={16} className="mr-2" />
                  Teilnehmer laden
                </Button>
                <Link to={`/events/${id}/checkin`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                  >
                    <QrCode size={16} className="mr-2" />
                    Check-in
                  </Button>
                </Link>
                <Button
                  onClick={() => setShowNewsletterModal(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                >
                  <Mail size={16} className="mr-2" />
                  Newsletter
                </Button>
                <Button
                  onClick={handleExport}
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                >
                  <FileDown size={16} className="mr-2" />
                  CSV Export
                </Button>
              </div>
            </div>

            {/* Admin Add Participant Form */}
            {showAdminAddForm && (
              <AdminAddParticipantForm eventId={id} getToken={getToken} onAdded={() => { setShowAdminAddForm(false); fetchRegistrations(); }} />
            )}

            {registrations && (
              <div className="space-y-6">
                {/* Confirmed */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-green-600 mb-3 flex items-center gap-2">
                    <CheckCircle size={16} />
                    Bestätigt ({registrations.confirmed?.length || 0})
                  </h3>
                  {registrations.confirmed?.length > 0 ? (
                    <div className="bg-card border border-border divide-y divide-border">
                      {registrations.confirmed.map(reg => (
                        <div key={reg.id} className="p-3 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{reg.member_name}</span>
                              <span className="text-muted-foreground text-sm">{reg.member_email}</span>
                              {reg.is_guest && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs font-medium">Gast</span>
                              )}
                              {reg.guests > 0 && (
                                <span className="text-sm text-muted-foreground">(+{reg.guests} Gäste)</span>
                              )}
                            </div>
                            {/* Payment method badge */}
                            <div className="flex items-center gap-2 mt-1">
                              {reg.payment_method === 'cash' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-700 text-xs rounded">
                                  <Banknote size={12} />
                                  Barzahlung
                                </span>
                              ) : reg.payment_status === 'paid' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-700 text-xs rounded">
                                  <CreditCard size={12} />
                                  Online bezahlt
                                </span>
                              ) : reg.payment_method === 'online' || reg.stripe_session_id ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-700 text-xs rounded">
                                  <CreditCard size={12} />
                                  Online
                                </span>
                              ) : null}
                              {reg.notes && (
                                <span className="text-xs text-muted-foreground italic truncate max-w-[200px]" title={reg.notes}>
                                  "{reg.notes}"
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleAdminCancelRegistration(reg.id)}
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive flex-shrink-0"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Keine bestätigten Anmeldungen</p>
                  )}
                </div>

                {/* Waitlist */}
                {registrations.waitlist?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-600 mb-3 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Warteliste ({registrations.waitlist?.length || 0})
                    </h3>
                    <div className="bg-card border border-border divide-y divide-border">
                      {registrations.waitlist.map(reg => (
                        <div key={reg.id} className="p-3 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{reg.member_name}</span>
                              <span className="text-muted-foreground text-sm">{reg.member_email}</span>
                              {reg.is_guest && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs font-medium">Gast</span>
                              )}
                              {reg.guests > 0 && (
                                <span className="text-sm text-muted-foreground">(+{reg.guests} Gäste)</span>
                              )}
                            </div>
                            {/* Payment method badge */}
                            <div className="flex items-center gap-2 mt-1">
                              {reg.payment_method === 'cash' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-700 text-xs rounded">
                                  <Banknote size={12} />
                                  Barzahlung
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-700 text-xs rounded">
                                  <CreditCard size={12} />
                                  Online
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              onClick={() => handleConfirmWaitlist(reg.id)}
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Bestätigen
                            </Button>
                            <Button
                              onClick={() => handleAdminCancelRegistration(reg.id)}
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cancelled */}
                {registrations.cancelled?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                      <X size={16} />
                      Abgemeldet ({registrations.cancelled.length})
                    </h3>
                    <div className="bg-card border border-border divide-y divide-border opacity-60">
                      {registrations.cancelled.map(reg => (
                        <div key={reg.id} className="p-3 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium line-through">{reg.member_name}</span>
                              <span className="text-muted-foreground text-sm">{reg.member_email}</span>
                              {reg.is_guest && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs font-medium">Gast</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-muted-foreground">
                                {reg.cancelled_at ? new Date(reg.cancelled_at).toLocaleString('de-CH') : ''}
                                {reg.cancelled_by ? ` (von ${reg.cancelled_by})` : ' (selbst abgemeldet)'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Registration Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-card border border-border w-full sm:max-w-xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col sm:rounded-none rounded-t-xl">
            {/* Header - Sticky */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border bg-secondary/30 flex-shrink-0">
              <h2 className="font-['Oswald'] text-xl sm:text-2xl font-bold uppercase tracking-wide">
                {isRegistered ? 'Anmeldung bearbeiten' : 'Event-Anmeldung'}
              </h2>
              <button onClick={() => setShowRegisterModal(false)} className="text-muted-foreground hover:text-foreground p-1">
                <X size={24} />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Event Info - Compact */}
              <div className="bg-secondary/20 p-3 border-l-4 border-primary">
                <p className="font-bold text-base">{event.title}</p>
                <p className="text-sm text-muted-foreground">
                  {eventDate.toLocaleDateString('de-CH', { weekday: 'short', day: 'numeric', month: 'long' })}
                  {event.time && ` • ${event.time}`}
                </p>
              </div>
              
              {/* Participant Selection - Compact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Ihre Anmeldung
                  </label>
                  <div className="bg-secondary/30 p-2.5 border border-border">
                    <p className="font-medium text-sm">1 Person (Sie)</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Zusätzliche Gäste
                  </label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value) || 0)}
                    className="w-full h-[42px] text-center text-lg font-bold border border-border bg-background px-3"
                    data-testid="guests-select"
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                  </select>
                </div>
              </div>
              
              {/* Notes - Compact */}
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Bemerkungen (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="z.B. Diätanforderungen, besondere Wünsche..."
                  rows={2}
                  className="text-sm"
                />
              </div>
              
              {/* Waitlist Warning - Compact */}
              {isFull && !isRegistered && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-600 flex items-start gap-2">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">Event ist ausgebucht</p>
                    <p className="text-xs">Sie werden auf die Warteliste gesetzt.</p>
                  </div>
                </div>
              )}

              {/* Payment Section - Clear Flow */}
              {requiresPayment && !isRegistered && (
                <div className="border-2 border-primary bg-primary/5 p-3 sm:p-4">
                  <h3 className="font-bold text-base sm:text-lg mb-3 flex items-center gap-2">
                    <CreditCard size={18} className="text-primary" />
                    Zahlungsübersicht
                  </h3>
                  
                  {/* Price Breakdown - Compact */}
                  <div className="space-y-1 mb-3 text-sm">
                    <div className="flex justify-between">
                      <span>Sie selbst</span>
                      <span>CHF {event.price?.toFixed(2)}</span>
                    </div>
                    {guests > 0 && (
                      <div className="flex justify-between">
                        <span>{guests} Gast/Gäste</span>
                        <span>CHF {(guests * event.price).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold text-base">
                      <span>Total</span>
                      <span className="text-primary">CHF {totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {/* Payment Method Selection - More Compact */}
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Zahlungsart wählen:</p>
                    <div className={`grid gap-2 ${(availablePaymentMethods.card || availablePaymentMethods.twint) && availablePaymentMethods.cash ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {(availablePaymentMethods.card || availablePaymentMethods.twint) && (
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('online')}
                        className={`p-3 border-2 text-left transition-all ${
                          paymentMethod === 'online' 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <CreditCard size={16} className={paymentMethod === 'online' ? 'text-primary' : 'text-muted-foreground'} />
                          <span className="font-semibold text-sm">Online bezahlen</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {[availablePaymentMethods.twint && 'TWINT', availablePaymentMethods.card && 'Kreditkarte'].filter(Boolean).join(' oder ')}
                        </p>
                      </button>
                      )}
                      {availablePaymentMethods.cash && (
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('cash')}
                        className={`p-3 border-2 text-left transition-all ${
                          paymentMethod === 'cash' 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Banknote size={16} className={paymentMethod === 'cash' ? 'text-primary' : 'text-muted-foreground'} />
                          <span className="font-semibold text-sm">Barzahlung vor Ort</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Bezahlung am Event</p>
                      </button>
                      )}
                    </div>
                  </div>
                  
                  {/* What happens next - only for online payment - Collapsed on mobile */}
                  {paymentMethod === 'online' && (
                    <div className="bg-background/50 p-3 border border-border text-sm">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Nach dem Klick auf "Weiter zur Zahlung":</p>
                      <div className="space-y-1.5 text-xs sm:text-sm">
                        <div className="flex items-start gap-2">
                          <span className="bg-primary text-primary-foreground w-4 h-4 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">1</span>
                          <span>Sie werden zu unserer sicheren Zahlungsseite weitergeleitet</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="bg-primary text-primary-foreground w-4 h-4 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">2</span>
                          <span>Wählen Sie {[availablePaymentMethods.twint && 'TWINT', availablePaymentMethods.card && 'Kreditkarte'].filter(Boolean).map((m, i, arr) => <span key={m}>{i > 0 && ' oder '}<strong>{m}</strong></span>)}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="bg-primary text-primary-foreground w-4 h-4 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">3</span>
                          <span>Nach erfolgreicher Zahlung sind Sie automatisch angemeldet</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Cash payment info */}
                  {paymentMethod === 'cash' && (
                    <div className="bg-yellow-500/10 p-3 border border-yellow-500/30">
                      <p className="text-sm text-yellow-700 flex items-start gap-2">
                        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <span>Sie bezahlen <strong>CHF {totalPrice.toFixed(2)}</strong> direkt vor Ort am Event.</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
              
            </div>
            
            {/* Action Buttons - Sticky Footer */}
            <div className="flex gap-3 sm:gap-4 p-4 sm:p-6 pt-3 sm:pt-4 border-t border-border bg-card flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowRegisterModal(false)}
                className="flex-1 h-11 sm:h-12 rounded-none text-sm sm:text-base"
              >
                Abbrechen
              </Button>
              <Button
                onClick={isRegistered ? handleUpdateRegistration : handleRegister}
                disabled={registering}
                className="flex-1 h-11 sm:h-12 rounded-none text-sm sm:text-base font-bold"
              >
                {registering ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : isRegistered ? (
                  'Speichern'
                ) : requiresPayment && paymentMethod === 'online' ? (
                  <>
                    Zur Zahlung
                    <CreditCard className="ml-2" size={18} />
                  </>
                ) : requiresPayment && paymentMethod === 'cash' ? (
                  <>
                    Anmelden
                    <Banknote className="ml-2" size={18} />
                  </>
                ) : isFull ? (
                  'Warteliste'
                ) : (
                  'Anmelden'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Modal */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase">Event-Newsletter</h2>
              <button onClick={() => { setShowNewsletterModal(false); setSendToAllMembers(false); }} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Recipient Selection */}
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Empfänger</label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setSendToAllMembers(false)}
                    className={`p-3 border text-left transition-colors ${
                      !sendToAllMembers 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="recipient-participants-only"
                  >
                    <p className="font-medium">Nur Teilnehmer</p>
                    <p className="text-xs text-muted-foreground">
                      {(stats.registrations_count || 0) + (stats.waitlist_count || 0)} Personen (bestätigt + Warteliste)
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendToAllMembers(true)}
                    className={`p-3 border text-left transition-colors ${
                      sendToAllMembers 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="recipient-all-members"
                  >
                    <p className="font-medium">Alle Mitglieder</p>
                    <p className="text-xs text-muted-foreground">
                      Informiert alle SHRT-Mitglieder über dieses Event
                    </p>
                  </button>
                </div>
              </div>

              {sendToAllMembers ? (
                /* Alle Mitglieder selected → show campaign creation button */
                <div className="space-y-4 pt-2">
                  <div className="bg-blue-500/10 border border-blue-500/30 p-4">
                    <p className="text-sm text-blue-400">
                      Es wird eine neue Kampagne erstellt mit vorausgefülltem Inhalt zu diesem Event. 
                      Sie können den Inhalt vor dem Versand noch anpassen.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => { setShowNewsletterModal(false); setSendToAllMembers(false); }}
                      className="flex-1 rounded-none"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleCreateEventCampaign}
                      disabled={creatingCampaign}
                      className="flex-1 rounded-none"
                      data-testid="create-event-campaign-btn"
                    >
                      {creatingCampaign ? (
                        <Loader2 className="animate-spin mr-2" size={16} />
                      ) : (
                        <Mail className="mr-2" size={16} />
                      )}
                      Newsletter erstellen
                    </Button>
                  </div>
                </div>
              ) : (
                /* Nur Teilnehmer selected → show subject/content fields */
                <>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Betreff</label>
                    <Input
                      value={newsletterSubject}
                      onChange={(e) => setNewsletterSubject(e.target.value)}
                      placeholder={`SHRT: ${event.title}`}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Nachricht</label>
                    <div className="mt-1">
                      <WysiwygEditor
                        value={newsletterContent}
                        onChange={(html) => setNewsletterContent(html)}
                        placeholder={`Inhalt eingeben...`}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowNewsletterModal(false)}
                      className="flex-1 rounded-none"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleSendNewsletter}
                      disabled={sendingNewsletter}
                      className="flex-1 rounded-none"
                    >
                      {sendingNewsletter ? (
                        <Loader2 className="animate-spin mr-2" size={16} />
                      ) : (
                        <Mail className="mr-2" size={16} />
                      )}
                      An Teilnehmer senden
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── Share Menu Content ──────────────────── */
function ShareMenuContent({ event, eventId, onClose, linkCopied, setLinkCopied }) {
  const publicUrl = `${window.location.origin}/public/event/${eventId}`;
  const d = new Date(event.date);
  const dateStr = d.toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const shareText = `${event.title} – ${dateStr}${event.time ? `, ${event.time} Uhr` : ''}${event.location ? `, ${event.location}` : ''}`;

  const handleWhatsApp = () => {
    const msg = `${shareText}\n\nInfos & Anmeldung:\n${publicUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    onClose();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(shareText);
    const body = encodeURIComponent(`${shareText}\n\nInfos & Anmeldung: ${publicUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    onClose();
  };

  const btnClass = "w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-muted transition-colors text-left";

  return (
    <>
      <button onClick={handleWhatsApp} className={btnClass} data-testid="share-menu-whatsapp">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp
      </button>
      <button onClick={handleCopy} className={btnClass} data-testid="share-menu-copy">
        {linkCopied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
        {linkCopied ? 'Link kopiert!' : 'Link kopieren'}
      </button>
      <button onClick={handleEmail} className={btnClass} data-testid="share-menu-email">
        <Mail size={18} />
        Per E-Mail teilen
      </button>
    </>
  );
}


function AdminAddParticipantForm({ eventId, getToken, onAdded }) {
  const [mode, setMode] = useState('member');
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestCount, setGuestCount] = useState(0);
  const [addNotes, setAddNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === 'member' && members.length === 0) {
      axios.get(`${API_URL}/api/members`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(res => setMembers(res.data.members || []))
        .catch(() => {});
    }
  }, [mode]);

  useEffect(() => {
    if (!search.trim()) { setFiltered([]); return; }
    const q = search.toLowerCase();
    setFiltered(members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 8));
  }, [search, members]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = getToken();
      if (mode === 'member') {
        if (!selectedMember) { toast.error('Bitte Mitglied auswählen'); setSaving(false); return; }
        await axios.post(`${API_URL}/api/events/${eventId}/registrations`,
          { member_id: selectedMember.id, guests: guestCount, notes: addNotes || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${selectedMember.name} wurde angemeldet`);
      } else {
        if (!guestName.trim()) { toast.error('Bitte Name eingeben'); setSaving(false); return; }
        await axios.post(`${API_URL}/api/events/${eventId}/registrations/guest`,
          { name: guestName, email: guestEmail || null, phone: guestPhone || null, guests: guestCount, notes: addNotes || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${guestName} wurde als Gast angemeldet`);
      }
      onAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anmelden');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-muted/20 border border-border p-4 mb-6 space-y-3" data-testid="admin-add-form">
      <div className="flex gap-1 bg-muted p-0.5 w-fit">
        <button type="button" onClick={() => { setMode('member'); setSelectedMember(null); }}
          className={`text-xs px-3 py-1.5 font-medium transition-colors ${mode === 'member' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
          Mitglied
        </button>
        <button type="button" onClick={() => { setMode('guest'); setSearch(''); }}
          className={`text-xs px-3 py-1.5 font-medium transition-colors ${mode === 'guest' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
          Gast
        </button>
      </div>
      {mode === 'member' ? (
        <div>
          {selectedMember ? (
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 p-2">
              <User size={14} className="text-primary" />
              <span className="text-sm font-medium flex-1">{selectedMember.name}</span>
              <span className="text-xs text-muted-foreground">{selectedMember.email}</span>
              <button type="button" onClick={() => { setSelectedMember(null); setSearch(''); }}><X size={14} /></button>
            </div>
          ) : (
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Mitglied suchen..."
                className="rounded-none pl-8" autoFocus />
              {filtered.length > 0 && (
                <div className="absolute z-20 w-full bg-card border border-border shadow-lg max-h-48 overflow-y-auto">
                  {filtered.map(m => (
                    <button key={m.id} type="button" onClick={() => { setSelectedMember(m); setSearch(''); setFiltered([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm flex items-center gap-2">
                      <User size={12} className="text-muted-foreground" />
                      <span className="font-medium">{m.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{m.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Name *" className="rounded-none" required />
          <Input value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="E-Mail" className="rounded-none" type="email" />
          <Input value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="Telefon" className="rounded-none" />
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Begleitung:</label>
          <Input value={guestCount} onChange={e => setGuestCount(Math.max(0, parseInt(e.target.value) || 0))}
            type="number" min="0" max="10" className="rounded-none w-16 h-8 text-sm" />
        </div>
        <Input value={addNotes} onChange={e => setAddNotes(e.target.value)} placeholder="Bemerkungen" className="rounded-none flex-1 h-8 text-sm" />
        <span className="text-xs text-muted-foreground">Barzahlung</span>
        <Button type="submit" size="sm" disabled={saving} className="rounded-none">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <UserPlus size={14} className="mr-1" />}
          Anmelden
        </Button>
      </div>
    </form>
  );
}
